import { useState } from "react";
import {
  Box,
  Avatar,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  alpha,
  TextField,
  ListSubheader,
  InputAdornment,
  Chip,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import ChatIcon from "@mui/icons-material/Chat";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSettings } from "../../../context/SettingsContext.jsx";
import { capitalize } from "../../../config/settings.config";

const normalizeKey = (s) => String(s || "").trim().toLowerCase().replace(/[\s_]+/g, "_");

const resolveLeadName = (lead) => {
  const cf = lead?.custom_fields || {};

  // Check direct custom_fields keys (could be "full_name" or "full name")
  for (const [k, v] of Object.entries(cf)) {
    if (normalizeKey(k) === "full_name" && v && typeof v === "string" && v.trim()) {
      return v.trim();
    }
  }

  // Extract from meta_raw_field_data array
  const raw = cf.meta_raw_field_data;
  if (Array.isArray(raw)) {
    for (const nameKey of ["full_name", "name"]) {
      const f = raw.find((item) => normalizeKey(item?.name) === nameKey);
      if (f?.values?.[0]) return String(f.values[0]).trim();
    }
    const first = raw.find((item) => normalizeKey(item?.name) === "first_name")?.values?.[0] || "";
    const last = raw.find((item) => normalizeKey(item?.name) === "last_name")?.values?.[0] || "";
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
  }

  const name = lead?.client_name || "";
  return name !== "Meta Lead" ? name : "";
};

export default function LeadHeaderLeft({
  lead,
  updateLead,
  STATUS,
  statusObj,
  isMobile,
  isTablet,
}) {
  const currentKey = lead?.current_status?.key || Object.keys(STATUS)[0] || "";
  const currentType = lead?.current_status?.type || "grade";
  const phone = lead?.contact;
  const whatsapp = lead?.contact;

  const settings = useSettings();
  const { dynamicStatus } = settings || {};

  const isLeadGradeStatus = ["new", "cold", "warm", "hot"].includes(currentKey);

  const handleQualify = () => {
    updateLead("current_status", {
      key: "qualified",
      type: "pipeline",
    });
  };

  const [statusSearch, setStatusSearch] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState(lead?.client_name || "");

  const leadGradeStatuses = ["new", "cold", "warm", "hot", "dropped"];
  const preConsultStatuses = ["qualified", "need_analysis", "booked", "dropped"];
  const postConsultStatuses = [
    "post_consult",
    "proposal_shared",
    "negotiation",
    "treatment_booked",
    "won",
    "dropped",
  ];

  const getAvailableStatuses = () => {
    if (currentKey === "dropped") {
      const timeline = lead?.timeline || [];
      const lastPipelineStatus = [...timeline]
        .reverse()
        .find(t => t.type === "pipeline" && t.key !== "dropped");

      if (lastPipelineStatus) {
        if (postConsultStatuses.includes(lastPipelineStatus.key)) {
          return postConsultStatuses;
        }
        return preConsultStatuses;
      }

      if (currentType === "pipeline") {
        return preConsultStatuses;
      }

      return leadGradeStatuses;
    }

    
    if (currentType === "grade") {
      
      return leadGradeStatuses;
    }

    if (currentType === "pipeline") {
      
      if (postConsultStatuses.includes(currentKey)) {
        return postConsultStatuses;
      }
      return preConsultStatuses;
    }

    return leadGradeStatuses;
  };

  const availableStatuses = getAvailableStatuses();

  const filteredStatuses = availableStatuses
    .map((key) => STATUS[key])
    .filter((st) => st && st.label.toLowerCase().includes(statusSearch.toLowerCase()));

  const handleNameSave = () => {
    if (tempName.trim()) {
      updateLead("client_name", tempName.trim());
    }
    setEditingName(false);
  };

  const handleNameCancel = () => {
    setTempName(lead?.client_name || "");
    setEditingName(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2.5,
        p: 2.5,
        minWidth: 380,
      }}
    >
      {/* Avatar with status indicator */}
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            fontSize: 24,
            fontWeight: 700,
            bgcolor: "primary.main",
            color: "#fff",
            boxShadow: (theme) =>
              `0 4px 12px ${theme.palette.primary.main}33`,
          }}
        >
          {resolveLeadName(lead)
            ? resolveLeadName(lead)
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()
            : "?"}
        </Avatar>

        {/* Status indicator */}
        <Box
          sx={{
            position: "absolute",
            left: -4,
            bottom: -4,
            borderRadius: "50%",
            bgcolor: "#fff",
            p: 0.35,
            boxShadow: `0 2px 8px ${alpha("#000", 0.12)}`,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <FormControl size="small" sx={{ width: "fit-content" }}>
            <Select
              value={currentKey}
              onChange={(e) => {
                const key = e.target.value;
                let type = "grade";
                if (preConsultStatuses.includes(key) || postConsultStatuses.includes(key)) {
                  type = "pipeline";
                }
                if (key === "dropped") {
                  type = currentType;
                }
                updateLead("current_status", {
                  key,
                  type,
                });
              }}
              onClose={() => setStatusSearch("")}
              displayEmpty
              IconComponent={() => null}
              sx={{
                minWidth: 24,
                width: 24,
                height: 24,
                borderRadius: "50%",
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: statusObj?.color || "#22C55E",
                border: "none",
                color: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-select": {
                  padding: "0 !important",
                  height: "100%",
                  minHeight: "unset",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                },
                "& .MuiSelect-select > *": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                "&:hover": { bgcolor: statusObj?.color || "#16A34A" },
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              renderValue={() => (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  {statusObj?.icon}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  sx: { mt: 1, borderRadius: 2, p: 0.5, minWidth: 220 },
                },
                autoFocus: false,
              }}
            >
              <ListSubheader sx={{ bgcolor: "transparent", p: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search status..."
                  value={statusSearch}
                  onChange={(e) => setStatusSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  fullWidth
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ fontSize: 20, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />
              </ListSubheader>
              {filteredStatuses.map((st) => (
                <MenuItem
                  key={st.key}
                  value={st.key}
                  sx={{ borderRadius: 1, mx: 0.5, my: 0.25 }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ color: st.color, fontSize: 20 }}>{st.icon}</Box>
                    <Typography fontSize={14} fontWeight={600}>
                      {capitalize(st.label)}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
              {filteredStatuses.length === 0 && (
                <MenuItem disabled sx={{ justifyContent: "center", py: 2 }}>
                  <Typography color="text.secondary" fontSize={14}>
                    No statuses found
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Name, Chips, and Action Buttons */}
      <Stack spacing={0.75}>
        {editingName ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              autoFocus
              size="small"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNameSave();
                if (e.key === "Escape") handleNameCancel();
              }}
              onBlur={handleNameSave}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 18,
                  fontWeight: 600,
                },
              }}
            />
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#1a1a2e",
                textTransform: "capitalize",
              }}
            >
              {resolveLeadName(lead) || "Unnamed Client"}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setTempName(lead?.client_name || "");
                setEditingName(true);
              }}
              sx={{
                width: 20,
                height: 20,
                color: "#9CA3AF",
                "&:hover": { color: "#6B7280" },
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        )}

        {/* Status Chip - Cold Lead format */}
        <Stack direction="row" spacing={0.75}>
          {statusObj && (
            <Chip
              label={capitalize(statusObj.label || currentKey)}
              size="small"
              sx={{
                bgcolor: statusObj?.color ? `${statusObj.color}20` : "#DBEAFE",
                color: statusObj?.color || "#2563EB",
                fontWeight: 600,
                fontSize: 11,
                height: 22,
                textTransform: "capitalize",
                border: "none",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}
        </Stack>

        {/* Action Buttons */}
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
          {isLeadGradeStatus && (
            <Button
              size="small"
              startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              onClick={handleQualify}
              sx={{
                height: 32,
                px: 1.5,
                bgcolor: "#059669",
                color: "#fff",
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 1.5,
                textTransform: "none",
                "&:hover": { bgcolor: "#047857" },
              }}
            >
              Qualify
            </Button>
          )}

          <IconButton
            component="a"
            href={`tel:${phone}`}
            size="small"
            sx={{
              width: 32,
              height: 32,
              bgcolor: "#22C55E",
              color: "#fff",
              "&:hover": { bgcolor: "#16A34A" },
            }}
          >
            <CallIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <IconButton
            component="a"
            target="_blank"
            href={`https://wa.me/91${whatsapp}`}
            size="small"
            sx={{
              width: 32,
              height: 32,
              bgcolor: "#F59E0B",
              color: "#fff",
              "&:hover": { bgcolor: "#D97706" },
            }}
          >
            <ChatIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <IconButton
            component="a"
            href={`mailto:${lead?.email || ""}`}
            size="small"
            sx={{
              width: 32,
              height: 32,
              bgcolor: "#3B82F6",
              color: "#fff",
              "&:hover": { bgcolor: "#2563EB" },
            }}
          >
            <EmailIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}
